import type { RiskScoreHistoryRow } from '@risksmart-app/trpc/src/types';
import type Highcharts from 'highcharts';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLineChartDefaultOptions } from 'src/components/highcharts';
import { RSHighcharts } from 'src/components/highcharts/RSHighcharts';
import { useColourPalette } from 'src/hooks/useColourPalette';

import { toLocalDate, toLocalDateTime } from '@/utils/dateUtils';

import { NoWidgetData } from '../../../../dashboards/widgets/NoWidgetData';
import { WidgetLoading } from '../../../../dashboards/widgets/widget-loading';
import { buildRiskScoreSeries } from './buildRiskScoreSeries';

type Props = {
  rows: RiskScoreHistoryRow[];
  loading?: boolean;
};

/**
 * Residual/inherent score trend, plotted with the low-level `RSHighcharts`
 * wrapper rather than `LineChartWidget`. `LineChartWidget` renders through
 * `HighchartsWidget`, which reads the dashboard store (`useDashboardStore`) for
 * its export wiring — that store is only initialised inside a dashboard, so
 * reusing it here threw "Must call setWidgets first!". This builds the same
 * line-chart options directly. Scores are fractional, so the y-axis allows
 * decimals (unlike the integer rating charts).
 */
const RiskScoreTrendChart: FC<Props> = ({ rows, loading }) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'scoreHistory.chart' });
  const { colours } = useColourPalette();
  const defaultOptions = useGetLineChartDefaultOptions({});

  if (loading) {
    return <WidgetLoading />;
  }

  const series = buildRiskScoreSeries(rows, {
    residual: t('residual'),
    inherent: t('inherent'),
  });

  if (series.length === 0) {
    return <NoWidgetData />;
  }

  // The datetime axis passes x/tick values as epoch-ms numbers; convert to an
  // ISO string for the shared date-formatting utils, guarding against a
  // non-finite value (which would make `new Date(NaN).toISOString()` throw).
  const formatAxisDate = (
    value: number | string | undefined,
    withTime: boolean
  ): string => {
    const ms = Number(value);
    if (!Number.isFinite(ms)) {
      return '';
    }
    const iso = new Date(ms).toISOString();

    return withTime ? toLocalDateTime(iso) : toLocalDate(iso);
  };

  const options: Highcharts.Options = {
    ...defaultOptions,
    // Suppress Highcharts' default "Chart title" — the section header already
    // labels this chart.
    title: { text: undefined },
    legend: { enabled: true },
    tooltip: {
      // Named `formatter` to match the property key (Datadog func-names +
      // func-name-matching); kept a function expression for Highcharts `this`.
      formatter: function formatter() {
        const xLabel = formatAxisDate(this.x, true);
        const yValue = typeof this.y === 'number' ? this.y.toFixed(1) : this.y;

        return `<b>${xLabel}</b><br/>${this.series.name}: ${yValue}`;
      },
    },
    xAxis: {
      type: 'datetime',
      minTickInterval: 24 * 3600 * 1000, // one day
      title: { text: t('xTitle') },
      labels: {
        formatter: function formatter() {
          return formatAxisDate(this.value, false);
        },
        rotation: -45,
      },
    },
    yAxis: {
      title: { text: t('yTitle') },
    },
    series: series.map((s, index) => ({
      type: 'line',
      name: s.name,
      data: s.data.map((d) => ({ x: new Date(d.x).getTime(), y: d.y })),
      color: colours[index % colours.length],
    })),
  };

  return (
    <RSHighcharts
      options={options}
      containerProps={{ style: { height: '360px' } }}
    />
  );
};

export default RiskScoreTrendChart;
